import React, { useEffect, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, notify, role_has } from "../../helpers";
import { ChevronLast, Eye, EyeOff, Loader, Lock, LogOut } from "lucide-react";
import Spacing from "../Spacing";
import { veradigmConnection } from "../../services/ehrConnection";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Config } from "../../Config";
import { getPracticeInfo } from "../../Session";
import { useLogout } from "../../hooks/useLogout";

export default function VeradigmConnection() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const navigate = useNavigate();
  let useInfo = getPracticeInfo();

  const portalLogout = useLogout();
  const [allSpeciality, setAllSpeciality] = useState([]);
  const [loginPassword, setLoginPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    account_id: useInfo?.account_id || null,
    user_id: useInfo?.user_id || null,
    ehr_type: "VeradigmEHR",
    ehr_username: useInfo?.ehr_username || "",
    ehr_password: "",
    speciality_ids: useInfo?.user_speciality || []
  });

  const handleSkip = () => {      
    navigate("/", { replace: true });
  };

  useEffect(() => {
    getAllSpeciality();
  }, [useInfo]);

  const getAllSpeciality = async () => {
    try {
      const response = getPractice("specialities");
      if (response?.length) {
        const allSpecialities = response?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setAllSpeciality(allSpecialities);
      };
    } catch (error) {
      // console.log("🚀 ~ getAllSpeciality: ~ error:", error);
    }
  };
  
  useEffect(() => {
    setCredentials((prev) => ({
      ...prev,
      account_id: useInfo?.account_id || null,
      user_id: useInfo?.user_id || null,
      ehr_username: useInfo?.ehr_username || "",
      speciality_ids: useInfo?.user_speciality || [32],
    }));
  }, [useInfo]);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSpecialityChange = (selectedOption) => {
    const selectedValues = selectedOption ? selectedOption?.map((option) => option?.value) : [];

    setCredentials({
      ...credentials,
      speciality_ids: selectedValues,
    });
  };

  const handleVeradignConnection = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await veradigmConnection(credentials);
      if (response) {
        if(response?.detail) {
          notify("error", response?.detail, 5);
        }
        if (response?.data?.status === "success") {
          notify("success", response?.data?.message);
          navigate("/", { replace: true });
        } else {
          notify("error", response?.message, 5);
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data ||
        "Oops! Server throws error";
      notify("error", errorMessage, 5);
    } finally {
      setLoading(false);
    }
  };

  useDocumentTitle("Connect with Veradigm EHR");

  return (
    <div className="docpro_login_wrap relative" style={{ height: "100vh" }}>
      <div className="docpro_login_right">
        <div className="docpro_login_card_wrap" style={{ flexDirection: "column", justifyContent: "start" }}>
          <div className="docpro_veradigm_connection_header">
            <div className="flex items-center docpro_grid_gap_10 docpro_ehr_buttons">
              {(can_visit("administrator") || role_has("Admin")) && (
                <button
                  onClick={handleSkip}
                  className="docpro_bordered_btn docpro_fw_500 docpro_white_color size_lg"
                >
                  <ChevronLast width={15} height={15} />
                  <span>Skip</span>
                </button>
              )}
              <button
                type="button"
                onClick={portalLogout}
                className="docpro_bordered_btn docpro_fw_500 docpro_white_color docpro_filed_btn size_lg"
                style={{ backgroundColor: '#e0483d', borderColor: '#c33f35' }}
              >
                <LogOut width={15} height={15} />
                <span>Log Out</span>
              </button>
            </div>
            <Spacing lg="45" sm="50" />
            <div className="docpro_text_center">
              <img src={`${Config.appUrl}/images/vaeradigm_connection.svg`} alt="Logo" />
              <Spacing lg="32" sm="32" />
              <h1 className="docpro_mb_0">Connect with Veradigm EHR</h1>
              <Spacing lg="10" sm="10" />
              <p>We sync patient schedules and push notes to EHR with this connection.</p>
            </div>
            <Spacing lg="25" sm="25" />
          </div>

          <div className="docpro_login_card">
            <form onSubmit={handleVeradignConnection} autoComplete="false">
              <label className="docpro_fw_500 docpro_primary_color">
                What’s your Veradigm EHR username? <span className="docpro_red_color">*</span>
              </label>
              <input
                type="text"
                className="docpro_form_field size_lg"
                placeholder="Enter your username"
                name="ehr_username"
                autoComplete="false"
                required
                value={credentials.ehr_username}
                onChange={handleChange}
              />

              <Spacing lg="20" sm="20" />
              <label className="docpro_fw_500 docpro_primary_color">
                What’s your Veradigm EHR password? <span className="docpro_red_color">*</span>
              </label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="ehr_password"
                  type={loginPassword ? "text" : "password"}
                  placeholder="••••••••"
                  autoComplete="false"
                  className="docpro_form_field size_lg"
                  required
                  value={credentials.ehr_password}
                  onChange={handleChange}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                { loginPassword ?
                  <Eye onClick={()=>setLoginPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                :
                  <EyeOff onClick={()=>setLoginPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                }
              </div>

              {allSpeciality?.length > 0 && (
                <div className="docpro_grid_item">
                  <Spacing lg="20" sm="20" />
                  <label className="docpro_fw_500 docpro_primary_color">
                    Specialities <span className="docpro_red_color">*</span>
                  </label>
                  <Select
                    isMulti
                    name="speciality_ids"
                    required
                    options={allSpeciality}
                    placeholder="Search..."
                    isSearchable
                    value={allSpeciality?.filter((option) => 
                      credentials?.speciality_ids?.includes(option?.value)
                    ) || []}
                    onChange={(selectedOption) => handleSpecialityChange(selectedOption)}
                    className="react-select-container w-full"
                    classNamePrefix="react-select"
                    filterOption={(option, rawInput) => {
                      const words = rawInput?.toLowerCase()?.split(" ");
                      return words?.every((word) =>
                        option?.label?.toLowerCase()?.includes(word)
                      );
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '40px',
                        borderColor: 'var(--border)',
                        borderRadius: '8px'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 1050,
                        marginTop: '-2px',
                        borderRadius: '0 0 3px 3px',
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1050,
                        padding: 0
                      }),
                    }}
                  />
                </div>
              )}
              <Spacing lg="25" sm="25" />

              <button
                className={`width_100 docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                style={{ pointerEvents: `${loading ? "none" : "initial"}` }}
                type="submit"
              >
                Connect with EHR
                {loading && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </form>
            <Spacing lg="30" sm="30" />
          </div>
        </div>
        <footer className="docpro_login_footer">
          <p>© {currentYear} DocPro. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
